<template>
    <div class="date-input-wrapper">
      <date-picker
        v-model="innerRange"                 
        range
        :disabled-date="disabledDate"
        :editable="false"
        :clearable="true"
        placeholder="Shipping ASAP"
        :format="displayFormat"              
        :value-type="'date'"                 
        :input-class="'bdp-input'"
        :popup-class="'bdp-popup'"
        :append-to-body="true"
        @change="onRangeChange"
      />
      <div v-if="hasValue" class="clear-popover">
        Clear Date
      </div>
    </div>
  </template>
  
  <script>
  import DatePicker from 'vue2-datepicker'
  import 'vue2-datepicker/index.css'
  
  export default {
    name: 'BusinessDateRangePicker',
    components: { DatePicker },
    props: {
      value: { type: String, default: '' },
      minBusinessDays: { type: Number, default: 3 },
      customMinDate: { type: [String, Date], default: null }
    },
    data() {
      const today = this.startOfDay(new Date())
      const max = this.addMonths(today, 3)
      
      // Use customMinDate if provided, otherwise use today
      let minDate = today
      if (this.customMinDate) {
        const parsed = this.parseCustomDate(this.customMinDate)
        if (parsed && !isNaN(parsed)) {
          minDate = this.startOfDay(parsed)
        }
      }
      
      // Initialize innerRange from value prop (if provided)
      let initialRange = this.parseIncoming(this.value)
  
      return {
        displayFormat: 'MM/DD/YYYY',   // shown in input
        minDate: minDate,
        maxDate: max,
        innerRange: initialRange
      }
    },
    computed: {
      hasValue() {
        return this.value && this.value !== ''
      }
    },
    watch: {
      value(v) {
        this.innerRange = this.parseIncoming(v);
      },
      customMinDate(newVal) {
        if (newVal) {
          const parsed = this.parseCustomDate(newVal)
          if (parsed && !isNaN(parsed)) {
            this.minDate = this.startOfDay(parsed)
          }
        } else {
          this.minDate = this.startOfDay(new Date())
        }
      }
    },
    methods: {
      parseCustomDate(dateInput) {
        if (!dateInput) return null
        
        let date
        if (typeof dateInput === 'string') {
          // Handle YYYY-MM-DD format
          if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
            const [year, month, day] = dateInput.split('-').map(Number)
            date = new Date(year, month - 1, day)
          }
          // Handle MM/DD/YYYY format
          else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateInput)) {
            const [month, day, year] = dateInput.split('/').map(Number)
            date = new Date(year, month - 1, day)
          }
          // Handle DD/MM/YYYY format
          else if (/^\d{2}-\d{2}-\d{4}$/.test(dateInput)) {
            const [day, month, year] = dateInput.split('-').map(Number)
            date = new Date(year, month - 1, day)
          }
          // Fallback to native Date parsing
          else {
            date = new Date(dateInput)
          }
        } else {
          date = dateInput
        }
        
        return date
      },
      
      startOfDay(d) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()) },
      addDays(d, n) { return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n) },
      addMonths(d, n) {
        const t = new Date(d.getFullYear(), d.getMonth(), d.getDate())
        t.setMonth(t.getMonth() + n)
        return this.startOfDay(t)
      },
      isWeekend(d) { const dow = d.getDay(); return dow === 0 || dow === 6 },
  
      addBusinessDays(date, n) {
        let d = this.startOfDay(date)
        let added = 0
        while (added < n) {
          d = this.addDays(d, 1)
          if (!this.isWeekend(d)) added++
        }
        return d
      },
  
      countBusinessDays(start, end) {
        let d = this.startOfDay(start)
        const last = this.startOfDay(end)
        let count = 0
        while (d < last) {
          d = this.addDays(d, 1)
          if (!this.isWeekend(d)) count++
        }
        return count
      },
  
      pad2(n) { return n < 10 ? '0' + n : '' + n },
      toMMDDYYYY(d) {
        return `${this.pad2(d.getMonth() + 1)}/${this.pad2(d.getDate())}/${d.getFullYear()}`
      },
  
      parseIncoming(v) {
        if (!v) return []
        const parts = v.split('-').map(s => s.trim())
        if (parts.length !== 2) return []
        const [a, b] = parts
        const pa = a.split('/').map(n => parseInt(n, 10))
        const pb = b.split('/').map(n => parseInt(n, 10))
        if (pa.length !== 3 || pb.length !== 3) return []
        // Parse as MM/DD/YYYY format: pa[0] = month, pa[1] = day, pa[2] = year
        const da = this.startOfDay(new Date(pa[2], pa[0] - 1, pa[1]))
        const db = this.startOfDay(new Date(pb[2], pb[0] - 1, pb[1]))
        if (isNaN(da) || isNaN(db)) return []
        return [da, db]
      },
  
      disabledDate(date) {
        const d = this.startOfDay(date)
        const outOfRange = d < this.minDate || d > this.maxDate
        return this.isWeekend(d) || outOfRange
      },
  
      onRangeChange(val) {
        if (!val || val.length === 0) {
          this.$emit('input', '')
          return
        }
        if (val.length === 1) {
          return
        }
  
        let [start, end] = val
        if (start > end) [start, end] = [end, start]
  
        const need = this.minBusinessDays
        const has = this.countBusinessDays(start, end)
        if (has < need) {
          end = this.addBusinessDays(start, need)
        }
  
        if (end > this.maxDate) {
          // (or choose to snap to max and still emit)
          this.innerRange = []
          this.$emit('input', '')
          return
        }
  
        this.innerRange = [start, end]
  
        const out = `${this.toMMDDYYYY(start)} - ${this.toMMDDYYYY(end)}`
        this.$emit('input', out)
        this.$emit('change', out)
      }
    }
  }
  </script>
  
  <style scoped>
  .date-input-wrapper {
    position: relative;
    width: 100%;
  }

  .date-input-wrapper:hover .clear-popover {
    opacity: 1;
    visibility: visible;
  }

  .clear-popover {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background-color: #0F0C38;
    color: white;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 12px;
    white-space: nowrap;
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease, visibility 0.2s ease;
    pointer-events: none;
    z-index: 1000;
  }

  .clear-popover::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #0F0C38;
  }

  .bdp-input {
    box-sizing: border-box;
    width: 100%;
    padding: 8px 40px 8px 12px; 
    border: 1px solid #cfcfcf;
    border-radius: 6px;
    background: #fff;
    color: #1a1a1a;
    font-size: 14px;
    line-height: 1.4;
  }
  
  .bdp-input:focus {
    outline: none;
    border-color: #5b9cff;
    box-shadow: 0 0 0 3px rgba(91,156,255,.2);
  }
  
  ::v-deep .mx-datepicker {
    position: relative;
  }
  ::v-deep .mx-datepicker .mx-icon-calendar {
    position: absolute;
    right: 10px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    color: #8a8a8a;
  }
  ::v-deep .mx-input {
    padding-right: 40px !important;
  }
  
  .bdp-popup .disabled {
    opacity: 0.45;
    cursor: not-allowed !important;
  }
  </style>
  